<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Toast Component
      p.text--secondary Display brief messages to users with different severity levels.

    // Toast Variations
    .component-section.mb-8#toast-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('toast-variations')") Toast Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in toastVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            yz-btn(:label="variation.buttonLabel" :color="variation.color" :text-color="variation.textColor" @click="showToast(variation.type, variation.message)")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API Reference
      
      .api-section.mb-6
        h4.mb-3 showMessage Action
        p.text--secondary Use the Vuex action to display toast messages with different types.
        
        .props-table
          v-simple-table
            template(v-slot:default)
              thead
                tr
                  th Parameter
                  th Type
                  th Required
                  th Description
              tbody
                tr(v-for="param in apiParams" :key="param.name")
                  td.font-weight-medium.text-no-wrap {{ param.name }}
                  td.text--secondary.text-no-wrap {{ param.type }}
                  td.text--secondary.text-no-wrap {{ param.required ? 'Required' : 'Optional' }}
                  td.single-line {{ param.description }}

    // Usage Examples
    .component-section.mb-8#usage-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('usage-examples')") Usage Examples
      
      .example-item.mb-6(v-for="example in usageExamples" :key="example.title")
        h4.mb-3 {{ example.title }}
        p.text--secondary.mb-3 {{ example.description }}
        
        .example-preview.mb-4.pa-4.border.rounded
          yz-btn(:label="example.buttonLabel" :color="example.color" :text-color="example.textColor" @click="showToast(example.type, example.message)")
        
        .example-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
            span.font-weight-medium.ml-2 Code
          
          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ example.code }}

</template>

<script>
import { mapActions } from 'vuex'

export default {
  data() {
    return {
      copiedCode: null,
      toastVariations: [
        {
          id: 'success-toast',
          name: 'Success Toast',
          description: 'Display success messages to users',
          type: 'success',
          message: 'Operation completed successfully!',
          buttonLabel: 'Show Success Toast',
          color: 'primary',
          code: `// Using mapActions
import { mapActions } from 'vuex'

export default {
  methods: {
    ...mapActions(['showMessage']),
    handleSuccess() {
      this.showMessage(['success', 'Operation completed successfully!'])
    }
  }
}`
        },
        {
          id: 'error-toast',
          name: 'Error Toast',
          description: 'Display error messages to users',
          type: 'error',
          message: 'Something went wrong. Please try again.',
          buttonLabel: 'Show Error Toast',
          color: 'danger',
          code: `// Using mapActions
import { mapActions } from 'vuex'

export default {
  methods: {
    ...mapActions(['showMessage']),
    handleError() {
      this.showMessage(['error', 'Something went wrong. Please try again.'])
    }
  }
}`
        },
        {
          id: 'info-toast',
          name: 'Info Toast',
          description: 'Display informational messages to users',
          type: 'info',
          message: 'Here is some useful information.',
          buttonLabel: 'Show Info Toast',
          color: 'blue-500',
          textColor: 'white',
          code: `// Using mapActions
import { mapActions } from 'vuex'

export default {
  methods: {
    ...mapActions(['showMessage']),
    handleInfo() {
      this.showMessage(['info', 'Here is some useful information.'])
    }
  }
}`
        }
      ],
      apiParams: [
        {
          name: 'array[0]',
          type: 'String',
          required: true,
          description: 'The type of toast message. Accepts: "success", "error", "info"'
        },
        {
          name: 'array[1]',
          type: 'String',
          required: true,
          description: 'The message text to display in the toast'
        }
      ],
      usageExamples: [
        {
          title: 'Form Validation Success',
          description: 'Show success message after form submission',
          type: 'success',
          message: 'Form submitted successfully!',
          buttonLabel: 'Submit Form',
          color: 'primary',
          code: `// Form submission handler
async submitForm() {
  try {
    await this.$api.submitForm(this.formData)
    this.showMessage(['success', 'Form submitted successfully!'])
  } catch (error) {
    this.showMessage(['error', 'Failed to submit form. Please try again.'])
  }
}`
        },
        {
          title: 'API Error Handling',
          description: 'Display error messages from API responses',
          type: 'error',
          message: 'Network error occurred',
          buttonLabel: 'Trigger API Error',
          color: 'danger',
          code: `// API call with error handling
async loadData() {
  try {
    this.loading = true
    const response = await this.$api.getData()
    this.data = response.data
  } catch (error) {
    this.showMessage(['error', error.response?.data?.message || 'Network error occurred'])
  } finally {
    this.loading = false
  }
}`
        },
        {
          title: 'User Notifications',
          description: 'Inform users about system status or updates',
          type: 'info',
          message: 'Your session will expire in 5 minutes',
          buttonLabel: 'Show Session Warning',
          color: 'blue-500',
          textColor: 'white',
          code: `// Session timeout warning
checkSessionTimeout() {
  const timeLeft = this.getSessionTimeLeft()
  if (timeLeft <= 5 * 60 * 1000) { // 5 minutes
    this.showMessage(['info', 'Your session will expire in 5 minutes'])
  }
}`
        }
      ]
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    showToast(type, message) {
      this.showMessage([type, message])
    },
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },
    navigateToSection(sectionId) {
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>

<style scoped>
.component-section {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 2rem;
}

.variation-item {
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.variation-preview {
  background-color: white;
}

.code-block {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.props-table .v-data-table th {
  font-weight: 600 !important;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
}

.example-item {
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.example-preview {
  background-color: white;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>